import React, { useState } from "react";
import { Calendar as CalIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import BrandCalendar from "@/components/BrandCalendar";
import { cn } from "@/lib/utils";

const fmtShort = (d) =>
  d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

// IMPORTANT — date serialization is local-time, NOT UTC.
//
// Previous regression: `toISOString().slice(0, 10)` converts the Date to UTC
// before slicing. For users east of UTC during the early morning, or west of
// UTC during late evening, a local-midnight Date becomes a *different
// calendar day* in UTC, so a click on "March 22" was being stored as
// "March 21". The local-time getters below preserve the calendar day the
// user actually clicked.
const toISO = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromISO = (s) => (s ? new Date(s + "T00:00:00") : undefined);

/**
 * Inline-styled trigger that opens the brand-aware Calendar in a popover.
 * Accepts ISO date strings (YYYY-MM-DD) so it drops into existing form state
 * without refactoring callers' shape.
 *
 * Modes:
 *   - mode="range" → value = { from: ISO, to: ISO }; onChange({from, to})
 *   - mode="single" → value = ISO; onChange(ISO)
 */
const DateRangePicker = ({
  mode = "range",
  value,
  onChange,
  placeholder = "Arrive — Depart",
  numberOfMonths = 2,
  className,
  triggerClassName,
  testId,
  disabled,
  align = "start",
}) => {
  const [open, setOpen] = useState(false);

  // Normalise the value into Date objects for DayPicker, and back to ISO on change.
  const dpValue =
    mode === "range"
      ? { from: fromISO(value?.from), to: fromISO(value?.to) }
      : fromISO(value);

  const display = (() => {
    if (mode === "range") {
      if (value?.from && value?.to)
        return `${fmtShort(fromISO(value.from))} → ${fmtShort(fromISO(value.to))}`;
      if (value?.from) return `${fmtShort(fromISO(value.from))} —`;
      return null;
    }
    return value ? fmtShort(fromISO(value)) : null;
  })();

  const handleSelect = (next) => {
    if (mode === "range") {
      onChange?.({
        from: toISO(next?.from),
        to: toISO(next?.to),
      });
      // Auto-close once both endpoints are set
      if (next?.from && next?.to) setOpen(false);
    } else {
      onChange?.(toISO(next));
      setOpen(false);
    }
  };

  const handleClear = () => {
    if (mode === "range") onChange?.({ from: "", to: "" });
    else onChange?.("");
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (mode === "range") {
      onChange?.({ from: toISO(today), to: "" });
    } else {
      onChange?.(toISO(today));
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "w-full h-11 px-3 border border-border bg-background text-foreground text-left",
            "text-[14px] font-ui inline-flex items-center gap-2 rounded-[var(--radius)]",
            "hover:border-foreground/40 focus-visible:outline-none focus-visible:border-foreground",
            "transition-colors",
            disabled && "opacity-60 cursor-not-allowed",
            triggerClassName
          )}
          data-testid={testId || "date-range-trigger"}
        >
          <CalIcon size={14} className="text-muted-foreground shrink-0" />
          {display ? (
            <span className="truncate">{display}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-auto p-0 border-0 bg-transparent shadow-none", className)} align={align}>
        <BrandCalendar
          mode={mode}
          numberOfMonths={numberOfMonths}
          selected={dpValue}
          onSelect={handleSelect}
          defaultMonth={
            mode === "range" ? fromISO(value?.from) : fromISO(value)
          }
          onClear={handleClear}
          onToday={handleToday}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
export { DateRangePicker };
